import { useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import { BookOpen, Zap } from "lucide-react";
import { Tooltip } from "../components/Tooltip";
import { useExperience } from "../context/ExperienceContext";
import { theme } from "../theme";

// ── Experience mode (Guided / Expert) ─────────────────────────────────────
// One segmented pill for both places the client shell offers it: the sidebar
// footer (always on screen) and the account menu. The state is
// `ExperienceContext`; this file only draws it.

export const GUIDED_DESCRIPTION = "Plain-language guidance, step-by-step";
export const EXPERT_DESCRIPTION = "Technical ISO/compliance terminology";

export function experienceModeDescription(novice: boolean): string {
  return novice ? GUIDED_DESCRIPTION : EXPERT_DESCRIPTION;
}

interface ExperienceModePillProps {
  /** Show the one-line description of the active mode under the pill. */
  describe?: boolean;
}

export function ExperienceModePill({ describe = true }: ExperienceModePillProps) {
  const { novice, toggle } = useExperience();
  const options: Array<{ key: string; label: string; icon: ReactNode; active: boolean }> = [
    { key: "guided", label: "Guided", icon: <BookOpen size={12} />, active: novice },
    { key: "expert", label: "Expert", icon: <Zap size={12} />, active: !novice },
  ];
  return (
    <div>
      <div role="group" aria-label="Experience mode" style={{ display: "flex", background: "var(--c-panelAlt)", border: "1px solid var(--c-border)", borderRadius: 3, padding: 3, gap: 2 }}>
        {options.map(({ key, label, icon, active }) => (
          <button
            key={key}
            type="button"
            aria-pressed={active}
            onClick={active ? undefined : toggle}
            style={{
              flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
              padding: "6px 0", borderRadius: 3, border: "none",
              cursor: active ? "default" : "pointer", fontSize: 11.5, fontWeight: active ? 700 : 400,
              color: active ? "var(--c-accentText)" : "var(--c-faint)",
              background: active ? "var(--c-panel)" : "transparent",
              boxShadow: active ? "inset 0 0 0 1px var(--c-line)" : "none",
              fontFamily: theme.font, transition: "all 0.15s",
            }}
          >
            <span style={{ display: "flex", lineHeight: 1 }}>{icon}</span>
            {label}
          </button>
        ))}
      </div>
      {describe && (
        <div style={{ fontSize: 10.5, color: "var(--c-faint)", marginTop: 6, lineHeight: 1.5 }}>
          {experienceModeDescription(novice)}
        </div>
      )}
    </div>
  );
}

interface ExperienceModeRailButtonProps {
  /** The rail's shared icon-button geometry (the sidebar owns it). */
  style: CSSProperties;
}

/**
 * The collapsed-rail form: one icon button showing the active mode whose
 * click switches to the other, with the tooltip saying which way it goes.
 */
export function ExperienceModeRailButton({ style }: ExperienceModeRailButtonProps) {
  const { novice, toggle } = useExperience();
  const [hov, setHov] = useState(false);
  const label = novice ? "Guided mode — switch to Expert" : "Expert mode — switch to Guided";
  return (
    <Tooltip label={label}>
      <button
        type="button"
        aria-label={label}
        aria-pressed={!novice}
        onClick={toggle}
        onMouseEnter={() => setHov(true)}
        onMouseLeave={() => setHov(false)}
        style={{ ...style, color: hov ? "var(--c-text)" : "var(--c-faint)", background: hov ? "var(--c-panelAlt)" : "transparent" }}
      >
        {novice ? <BookOpen size={14} /> : <Zap size={14} />}
      </button>
    </Tooltip>
  );
}
